import { ChangeDetectorRef, Directive, inject } from '@angular/core';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { untilDestroyed } from '@ngneat/until-destroy';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { ICellRendererParams, IRowNode } from 'ag-grid-community';
import { BehaviorSubject, debounceTime, map, merge, Observable, shareReplay, switchMap, tap } from 'rxjs';
import { TreeNode, TreeNodeMetadata } from '../tree-node.model';

@Directive()
export abstract class SelectableTreeCellRendererBaseComponent<
  OriginalData = unknown,
  Metadata extends TreeNodeMetadata | void = void,
> implements ICellRendererAngularComp
{
  constructor() {
    merge(this.bindAgGridActions(), this.updateStateWhenCellRendererParamsChange())
      .pipe(untilDestroyed(this))
      .subscribe();
  }

  protected readonly cdr = inject(ChangeDetectorRef);

  protected readonly isExpanded$$ = new BehaviorSubject(false);
  protected readonly isSelected$$ = new BehaviorSubject(false);
  protected readonly isIndeterminate$$ = new BehaviorSubject(false);

  protected readonly cellRendererParams$$ = new BehaviorSubject<ICellRendererParams<
    TreeNode<OriginalData, Metadata>,
    unknown,
    unknown
  > | null>(null);

  protected readonly node$ = this.cellRendererParams$$.pipe(
    debounceTime(0),
    map((params) => params?.node.data),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  protected readonly nestingLevel$ = this.cellRendererParams$$.pipe(map((params) => params?.node?.level ?? 0));
  protected readonly isGroup$ = this.cellRendererParams$$.pipe(map((params) => params?.node?.group ?? false));

  agInit(params: ICellRendererParams<TreeNode<OriginalData, Metadata>, unknown>): void {
    this.cellRendererParams$$.next(params);
  }

  refresh(params: ICellRendererParams<TreeNode<OriginalData, Metadata>, unknown>): boolean {
    this.cellRendererParams$$.next(params);
    return true;
  }

  onCheckboxChange(event: MatCheckboxChange): void {
    const params = this.cellRendererParams$$.value;
    if (params && params.node) {
      params.node.setSelected(event.checked);

      // When a group is toggled, AG Grid updates children. We need to manually
      // force an update on the indeterminate state because 'rowSelected' events
      // for this node might fire before children are fully updated or vice-versa.
      // However, if we just set selected=false, we KNOW it shouldn't be indeterminate immediately
      // if it's a full deselect action initiated by the user on this node.
      if (!event.checked) {
        this.isIndeterminate$$.next(false);
      } else {
        this.calculateSelectionState(params.node);
      }
    }
  }

  protected calculateSelectionState(node: IRowNode<TreeNode<OriginalData, Metadata>>) {
    const isSelected = node.isSelected() ?? false;
    this.isSelected$$.next(isSelected);

    // If it's a group node, check children for partial selection (indeterminate state)
    // Note: When a parent is deselected via checkbox click, the children deselect asynchronously in AG Grid.
    // However, when 'isSelected' is false for a group node, we need to be careful about checking children state immediately.
    // If this was triggered by a user click on this specific node, AG Grid's default selection behavior
    // for groupSelectsChildren should handle the children state updates, but we might be reading them too early
    // or they might still be reporting as selected during the event propagation.

    if (!isSelected && node.childrenAfterGroup) {
      // We check all leaf children to determine if ANY are selected.
      // If ANY are selected, then the group is indeterminate.
      const children = node.allLeafChildren;
      if (children && children.length > 0) {
        // Force a check on the updated state if possible, but standard access should work
        // if the event loop has processed the selection.
        const someSelected = children.some((child) => child.isSelected());
        this.isIndeterminate$$.next(someSelected);
        return;
      }
    }

    this.isIndeterminate$$.next(false);
  }

  protected updateStateWhenCellRendererParamsChange() {
    return this.cellRendererParams$$.pipe(
      tap((params) => {
        this.isExpanded$$.next(params?.node?.expanded ?? false);
        if (params?.node) {
          this.calculateSelectionState(params.node);
        }
      }),
    );
  }

  protected bindAgGridActions() {
    return this.cellRendererParams$$.pipe(
      switchMap((params) => {
        const node = params?.node;
        const api = params?.api;
        if (!node || !api) {
          return [];
        }

        return new Observable<void>(() => {
          const onSelectionChanged = () => {
            this.calculateSelectionState(node);
            this.cdr.markForCheck();
          };
          const onExpandedChanged = () => {
            this.isExpanded$$.next(node.expanded);
            this.cdr.markForCheck();
          };

          // Listen to global selectionChanged on the API so ALL cells update when ANY selection changes
          api.addEventListener('selectionChanged', onSelectionChanged);
          node.addEventListener('expandedChanged', onExpandedChanged);

          return () => {
            api.removeEventListener('selectionChanged', onSelectionChanged);
            node.removeEventListener('expandedChanged', onExpandedChanged);
          };
        });
      }),
    );
  }
}
